import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getDocs, collection, query, orderBy, limit } from 'firebase/firestore';
import { Button, Input, LoadingSpinner } from '../../components/ui';
import { useAuth } from '../../hooks/useAuth';
import { blogService } from '../../services/blogService';
import { storage, db } from '../../utils/firebaseConfig';
import type { Event } from '../../services/eventService';
import type { BlogPost } from '../../types/blog';

export function EditBlogPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isAdmin: userIsAdmin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadingBlog, setLoadingBlog] = useState(true);
  const [error, setError] = useState('');
  const [events, setEvents] = useState<Event[]>([]);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [existingImageUrl, setExistingImageUrl] = useState<string>('');

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    featuredImage: null as File | null,
    featuredEvents: [] as string[],
    tags: [] as string[],
    status: 'draft' as 'draft' | 'published',
  });

  const [tagInput, setTagInput] = useState('');

  // Load the blog post
  useEffect(() => {
    const loadBlog = async () => {
      if (!id || !userIsAdmin) return;

      try {
        setLoadingBlog(true);
        const blogData = await blogService.getBlogPost(id);

        if (!blogData) {
          setError('Blog post not found');
          return;
        }

        setFormData({
          title: blogData.title,
          content: blogData.content,
          excerpt: blogData.excerpt,
          featuredImage: null,
          featuredEvents: blogData.featuredEvents,
          tags: blogData.tags,
          status: blogData.status,
        });

        if (blogData.featuredImage) {
          setExistingImageUrl(blogData.featuredImage);
          setImagePreview(blogData.featuredImage);
        }
      } catch (err) {
        console.error('Error loading blog:', err);
        setError('Failed to load blog post');
      } finally {
        setLoadingBlog(false);
      }
    };

    loadBlog();
  }, [id, userIsAdmin]);

  // Load recent events for selection
  useEffect(() => {
    const loadEvents = async () => {
      try {
        const q = query(
          collection(db, 'events'),
          orderBy('createdAt', 'desc'),
          limit(50)
        );
        const querySnapshot = await getDocs(q);
        const eventsData = querySnapshot.docs.map(
          (doc) =>
            ({
              id: doc.id,
              ...doc.data(),
            } as Event)
        );
        setEvents(eventsData);
      } catch (error) {
        console.error('Error loading events:', error);
      }
    };

    if (userIsAdmin) {
      loadEvents();
    }
  }, [userIsAdmin]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData((prev) => ({ ...prev, featuredImage: file }));
      setExistingImageUrl('');

      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const handleEventToggle = (eventId: string) => {
    setFormData((prev) => ({
      ...prev,
      featuredEvents: prev.featuredEvents.includes(eventId)
        ? prev.featuredEvents.filter((id) => id !== eventId)
        : [...prev.featuredEvents, eventId],
    }));
  };

  const uploadImage = async (file: File): Promise<string> => {
    const filename = `blog-images/${Date.now()}_${file.name}`;
    const imageRef = ref(storage, filename);
    const snapshot = await uploadBytes(imageRef, file);
    return await getDownloadURL(snapshot.ref);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !userIsAdmin || !id) return;

    setError('');
    setLoading(true);

    try {
      if (!formData.title.trim()) {
        throw new Error('Blog title is required');
      }
      if (!formData.content.trim()) {
        throw new Error('Blog content is required');
      }
      if (!formData.excerpt.trim()) {
        throw new Error('Blog excerpt is required');
      }

      let imageUrl = existingImageUrl;
      if (formData.featuredImage) {
        imageUrl = await uploadImage(formData.featuredImage);
      }

      await blogService.updateBlogPost(id, {
        title: formData.title.trim(),
        content: formData.content.trim(),
        excerpt: formData.excerpt.trim(),
        featuredImage: imageUrl,
        featuredEvents: formData.featuredEvents,
        tags: formData.tags,
        status: formData.status,
      });

      // Redirect to blog view if published, or manage screen if draft
      if (formData.status === 'published') {
        navigate(`/blog/${id}`);
      } else {
        navigate('/admin/blog');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update blog post. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!userIsAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to edit blog posts.</p>
        </div>
      </div>
    );
  }

  if (loadingBlog) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center space-y-4">
          <LoadingSpinner size="lg" />
          <p className="text-gray-600">Loading blog post...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-0 pb-12">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Edit Blog Post</h1>
        <p className="text-gray-600 mt-2">Update your blog post content and settings</p>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm">
              {error}
            </div>
          )}

          {/* Blog Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
              Blog Title *
            </label>
            <Input
              id="title"
              type="text"
              value={formData.title}
              onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
              placeholder="Enter blog post title"
              required
              className="w-full"
            />
          </div>

          {/* Excerpt */}
          <div>
            <label htmlFor="excerpt" className="block text-sm font-medium text-gray-700 mb-2">
              Excerpt (Short Summary) *
            </label>
            <textarea
              id="excerpt"
              value={formData.excerpt}
              onChange={(e) => setFormData((prev) => ({ ...prev, excerpt: e.target.value }))}
              placeholder="Write a brief summary of this blog post..."
              required
              rows={2}
              maxLength={200}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
            />
            <p className="text-xs text-gray-500 mt-1">{formData.excerpt.length}/200 characters</p>
          </div>

          {/* Blog Content */}
          <div>
            <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-2">
              Content *
            </label>
            <textarea
              id="content"
              value={formData.content}
              onChange={(e) => setFormData((prev) => ({ ...prev, content: e.target.value }))}
              placeholder="Write your blog content here... You can use markdown formatting."
              required
              rows={12}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors font-mono text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">Supports basic markdown formatting</p>
          </div>

          {/* Featured Image */}
          <div>
            <label htmlFor="image" className="block text-sm font-medium text-gray-700 mb-2">
              Featured Image
            </label>
            <input
              id="image"
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
            />
            {imagePreview && (
              <div className="mt-4">
                <p className="text-sm font-medium text-gray-700 mb-3">Preview:</p>
                <div className="relative w-full aspect-video rounded-xl overflow-hidden border-2 border-gray-200">
                  <img src={imagePreview} alt="Blog preview" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                  <button
                    type="button"
                    onClick={() => {
                      setFormData((prev) => ({ ...prev, featuredImage: null }));
                      setImagePreview(null);
                      setExistingImageUrl('');
                    }}
                    className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-2 rounded-lg transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Featured Events */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Featured Events (Select events to highlight in this blog)
            </label>
            <div className="border border-gray-300 rounded-xl p-4 max-h-64 overflow-y-auto">
              {events.length === 0 ? (
                <p className="text-gray-500 text-sm">No events available</p>
              ) : (
                <div className="space-y-2">
                  {events.map((event) => (
                    <label key={event.id} className="flex items-start space-x-3 p-2 hover:bg-gray-50 rounded-lg cursor-pointer">
                      <input
                        type="checkbox"
                        checked={event.id ? formData.featuredEvents.includes(event.id) : false}
                        onChange={() => event.id && handleEventToggle(event.id)}
                        className="mt-1 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{event.title}</p>
                        <p className="text-xs text-gray-500">
                          {event.startDate} • {event.location?.city || event.city?.name || 'Unknown'}
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">{formData.featuredEvents.length} event(s) selected</p>
          </div>

          {/* Tags */}
          <div>
            <label htmlFor="tags" className="block text-sm font-medium text-gray-700 mb-2">
              Tags
            </label>
            <div className="flex flex-wrap gap-2 mb-3">
              {formData.tags.map((tag) => (
                <span key={tag} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-100 text-primary-800">
                  {tag}
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="ml-2 text-primary-600 hover:text-primary-800"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                placeholder="Add a tag..."
                className="flex-1"
              />
              <Button type="button" variant="outline" onClick={handleAddTag}>
                Add
              </Button>
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Publication Status</label>
            <div className="space-y-3">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="status"
                  checked={formData.status === 'draft'}
                  onChange={() => setFormData((prev) => ({ ...prev, status: 'draft' }))}
                  className="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-700">Save as Draft</span>
              </label>

              <label className="flex items-center">
                <input
                  type="radio"
                  name="status"
                  checked={formData.status === 'published'}
                  onChange={() => setFormData((prev) => ({ ...prev, status: 'published' }))}
                  className="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-700">Publish</span>
              </label>
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end gap-4 pt-6">
            <Button type="button" variant="outline" onClick={() => navigate('/admin/blog')}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="min-w-[120px]">
              {loading ? (
                <div className="flex items-center justify-center space-x-2">
                  <LoadingSpinner size="sm" />
                  <span>Saving...</span>
                </div>
              ) : formData.status === 'published' ? (
                'Save & Publish'
              ) : (
                'Save Draft'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
